import type {
  CreateAgentConfigurationVersion,
  ExecutionConfig,
  LLMConfig,
  ToolConfig,
} from '@mai/types';

import type { AGENT_TYPE_PRESETS } from '../../CreateAgentForm/presets';

export interface AgentFormData {
  // Basic info
  name: string;
  description?: string;

  // Type selection
  agentType: keyof typeof AGENT_TYPE_PRESETS | 'custom';

  // Instructions
  instructions: string;
  systemPrompt?: string;

  // Advanced config
  tools: ToolConfig;
  llmConfig: LLMConfig;
  executionConfig: ExecutionConfig;

  // I/O Schemas
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
}

export const defaultFormData: AgentFormData = {
  name: '',
  description: '',
  agentType: 'qa',
  instructions: '',
  systemPrompt: '',
  tools: [],
  llmConfig: {
    thought_llm: {
      provider: 'vertexai',
      model_tier: 'large',
      enable_reasoning: false,
    },
    fact_extraction_llm: {
      provider: 'vertexai',
      model_tier: 'large',
      enable_reasoning: false,
    },
    planner_llm: {
      provider: 'vertexai',
      model_tier: 'large',
      enable_reasoning: false,
    },
  },
  executionConfig: {
    max_actions: 10,
    planning_interval: 4,
    feedback_rounds: 0,
    timeout_minutes: 30,
  },
  inputSchema: {
    type: 'object',
    properties: {
      input: { type: 'string', description: 'User input' },
    },
    required: ['input'],
  },
  outputSchema: {
    type: 'object',
    properties: {
      output: { type: 'string', description: 'Agent response' },
    },
    required: ['output'],
  },
};

export const getFieldsForStep = (step: number): string[] => {
  switch (step) {
    case 0:
      return ['name', 'description'];
    case 1:
      return ['agentType'];
    case 2:
      return ['instructions'];
    case 3:
      return [];
    default:
      return [];
  }
};

export const isNextDisabled = (
  currentStep: number,
  agentType?: string,
): boolean => {
  if (currentStep === 1 && !agentType) {
    return true;
  }
  return false;
};

export const getNextButtonText = (
  currentStep: number,
  isPending: boolean,
): string => {
  if (currentStep === 3) {
    return isPending ? 'Creating...' : 'Create Agent';
  }
  return 'Next';
};

export const createConfigurationData = (
  agentId: string,
  values: AgentFormData,
): CreateAgentConfigurationVersion => ({
  agentId,
  instructions: values.instructions,
  inputSchema: values.inputSchema,
  outputSchema: values.outputSchema,
  versionNumber: 1,
  isCurrent: true,
  systemPrompt: values.systemPrompt || null,
  llmConfig: values.llmConfig,
  toolConfig: values.tools,
  executionConfig: values.executionConfig,
});
